import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ApiService from "../api/ApiServices";
import { useAppSelector } from "../redux/store";
import { tabData } from "../redux/rootSlice";
import { AppPaths } from "../utils/AppPaths";
import { applyReferralCode, ReferralInfo, shareReferral } from "../utils/referral";
import { ReferralInviteModal, ReferralNotificationModal } from "./ReferralModals";

type ReferralModalItem =
  | { kind: "joined" | "rewarded"; referredUserId: string; username: string }
  | { kind: "prompt" };

const TAB_PATHS = tabData.map((tab) => tab.path);

const itemKey = (item: ReferralModalItem) =>
  item.kind === "prompt" ? "prompt" : `${item.kind}:${item.referredUserId}`;

// Shows referral popups (friend joined, bonus points, invite prompt) on the
// bottom tab screens. The web app has no push notifications, so this is how
// users find out, and it refreshes when they come back to the browser tab.
const ReferralModalHost = () => {
  const { pathname } = useLocation();
  const { emojiModal, referralModalBlocked } = useAppSelector((state) => state.root);
  const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null);
  const [referralQueue, setReferralQueue] = useState<ReferralModalItem[]>([]);
  // Dismissed items stay hidden even if a refetch lands before markReferralSeen does
  const dismissedRef = useRef<Set<string>>(new Set());

  const isTabScreen = TAB_PATHS.includes(pathname);

  const getReferralInfo = useCallback(async () => {
    try {
      const response = await ApiService.getReferralInfo();
      if (!response?.status) return;
      const data: ReferralInfo = response.data;
      setReferralInfo(data);
      const queue: ReferralModalItem[] = [
        ...(data?.notifications?.joined || []).map((n) => ({ kind: "joined" as const, ...n })),
        ...(data?.notifications?.rewarded || []).map((n) => ({ kind: "rewarded" as const, ...n })),
        ...(data?.showReferralPrompt ? [{ kind: "prompt" as const }] : []),
      ];
      setReferralQueue(queue.filter((item) => !dismissedRef.current.has(itemKey(item))));
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (isTabScreen) getReferralInfo();
  }, [pathname, isTabScreen, getReferralInfo]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && isTabScreen) getReferralInfo();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [isTabScreen, getReferralInfo]);

  const currentReferralModal = referralQueue[0];
  // The invite prompt stays on Home; friend joined / bonus popups show on any tab
  const canShowReferralModal =
    Boolean(currentReferralModal) &&
    isTabScreen &&
    !referralModalBlocked &&
    !emojiModal &&
    (currentReferralModal?.kind !== "prompt" || pathname === AppPaths.HOME);

  const dismissReferralModal = () => {
    const item = referralQueue[0];
    if (!item) return;
    dismissedRef.current.add(itemKey(item));
    setReferralQueue((queue) => queue.slice(1));
    const request =
      item.kind === "prompt"
        ? { type: "prompt" as const }
        : { type: item.kind, referredUserIds: [item.referredUserId] };
    ApiService.markReferralSeen(request).catch(() => {});
  };

  const onReferralInvite = async () => {
    dismissReferralModal();
    await shareReferral(referralInfo);
  };

  const onApplyReferralCode = async (code: string) => {
    const { error, invitedBy } = await applyReferralCode(code, referralInfo?.rewardPoints?.referredUser);
    if (!error && invitedBy) {
      setReferralInfo((info) => info && { ...info, canApplyReferralCode: false, invitedBy });
    }
    return error;
  };

  return (
    <>
      <ReferralInviteModal
        visible={canShowReferralModal && currentReferralModal?.kind === "prompt"}
        points={referralInfo?.rewardPoints?.referrer}
        onClose={dismissReferralModal}
        onInvite={onReferralInvite}
        onApplyCode={referralInfo?.canApplyReferralCode ? onApplyReferralCode : undefined}
      />
      {currentReferralModal && currentReferralModal.kind !== "prompt" && (
        <ReferralNotificationModal
          visible={canShowReferralModal}
          type={currentReferralModal.kind}
          username={currentReferralModal.username}
          points={referralInfo?.rewardPoints?.referrer}
          onClose={dismissReferralModal}
          onInvite={onReferralInvite}
        />
      )}
    </>
  );
};

export default ReferralModalHost;
